import type { ListFilterFieldDef, ListFilterValue, ListQueryRequest, ListSortState } from './types';

/**
 * Filtry přišpendlené hned po načtení (příznak `pinned` v definici pole) — prázdné a připnuté.
 * Nasazují se na systémový pohled „Vše"; do dotazu nevstupují (nejsou aktivní).
 */
export function defaultPinnedFilters(defs: ListFilterFieldDef[]): ListFilterValue[] {
  return defs.filter((def) => def.pinned).map((def) => ({ ...blankFilter(def), pinned: true }));
}

/** Prázdný filtr daného pole — existuje ve stavu (typicky připnutý), výsledek neomezuje. */
export function blankFilter(def: ListFilterFieldDef): ListFilterValue {
  switch (def.type) {
    case 'enum':
      return { field: def.key, ids: [] };
    case 'selector':
      return { field: def.key, ids: [], items: [] };
    case 'text':
      return { field: def.key, op: 'contains', text: '' };
    case 'number':
      return { field: def.key, op: 'gte', number: null };
    case 'daterange':
      return { field: def.key };
  }
}

/** Omezuje filtr výsledek (má hodnotu)? Prázdný filtr je no-op a do dotazu se nepromítá. */
export function isFilterActive(
  def: ListFilterFieldDef | undefined,
  value: ListFilterValue
): boolean {
  if (!def) {
    return false;
  }
  switch (def.type) {
    case 'enum':
    case 'selector':
      return Array.isArray(value.ids) && value.ids.length > 0;
    case 'text':
      return Boolean(value.text && value.text.trim());
    case 'number':
      return value.number !== null && value.number !== undefined;
    case 'daterange':
      return Boolean(value.from || value.to);
  }
}

/** Kanonický tvar filtrů — bez `pinned`/`items`, seřazeno dle pole a s seřazenými `ids`. */
function canonicalFilters(filters: ListFilterValue[]): ListFilterValue[] {
  return filters
    .map((f) => ({
      field: f.field,
      op: f.op,
      ids: f.ids ? [...f.ids].sort() : undefined,
      text: f.text?.trim(),
      number: f.number,
      from: f.from,
      to: f.to,
    }))
    .sort((a, b) => a.field.localeCompare(b.field));
}

function effectiveFilters(
  filters: ListFilterValue[],
  defs: Map<string, ListFilterFieldDef>
): ListFilterValue[] {
  return canonicalFilters(filters.filter((f) => isFilterActive(defs.get(f.field), f)));
}

/**
 * Klíč efektivního dotazu — kanonická serializace neprázdných filtrů (seřazeno dle pole,
 * enum id seřazená, bez `pinned`) + normalizovaný fulltext. Dva dotazy se stejným klíčem
 * vracejí stejný výsledek (anatomie 02 §1) — řídí dirty detekci, režim Živě i React Query klíč.
 */
export function effectiveQueryKey(
  filters: ListFilterValue[],
  fulltext: string,
  defs: Map<string, ListFilterFieldDef>
): string {
  return JSON.stringify({
    filters: effectiveFilters(filters, defs),
    fulltext: fulltext.trim().replace(/\s+/g, ' ').toLowerCase(),
  });
}

/**
 * Klíč systémového scope (`scope` v configu šablony). Do `effectiveQueryKey` scope ZÁMĚRNĚ
 * nevstupuje — není součástí uživatelského dotazu (dirty detekce pohledů, persistence), jen
 * ho rámuje. Do klíče React Query dotazu vstupuje samostatně, jinak by výsledky dvou scopů
 * sdílely jeden cache záznam.
 */
export function scopeKey(scope: ListFilterValue[]): string {
  return JSON.stringify(canonicalFilters(scope));
}

/**
 * Sestaví request list dotazu z aktuálního stavu (aktivní filtry bez `pinned`).
 * Systémový `scope` se přimíchá vždy a přebíjí uživatelský filtr stejného pole.
 */
export function buildListRequest(
  filters: ListFilterValue[],
  fulltext: string,
  sort: ListSortState,
  page: number,
  pageSize: number,
  visibleColumns: string[],
  defs: Map<string, ListFilterFieldDef>,
  scope: ListFilterValue[] = []
): ListQueryRequest {
  const scopeFilters = canonicalFilters(scope);
  const scopeFields = new Set(scopeFilters.map((f) => f.field));
  return {
    filters: [
      ...effectiveFilters(filters, defs).filter((f) => !scopeFields.has(f.field)),
      ...scopeFilters,
    ],
    fulltext: fulltext.trim() || null,
    sort: { column: sort.column, descending: sort.desc },
    page,
    pageSize,
    columns: visibleColumns,
  };
}

const DATE_SUMMARY = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric' });

function formatDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? iso : DATE_SUMMARY.format(date);
}

export interface ChipSummaryTexts {
  all: string;
  from: (value: string) => string;
  to: (value: string) => string;
  selected: (count: number) => string;
}

/**
 * Souhrn hodnoty na chipu (anatomie 01 §3): enum „vše" / „A, B" / „A +2";
 * text „~ hodnota" / „= hodnota"; číslo „≥ 30"; rozsah „1.7.–31.8." / „od 1.7.".
 */
export function chipSummary(
  def: ListFilterFieldDef,
  value: ListFilterValue,
  texts: ChipSummaryTexts,
  enumLabels?: Map<string, string>
): string {
  switch (def.type) {
    case 'enum':
    case 'selector': {
      const ids = value.ids ?? [];
      if (ids.length === 0) {
        return texts.all;
      }
      // Selector pole: labely synchronně z uložených *Ref modelů (žádná rehydratace).
      const labelMap =
        def.type === 'selector'
          ? new Map(
              (value.items ?? []).map((item) => [String(def.getId(item)), def.getLabel(item)])
            )
          : enumLabels;
      if (!labelMap) {
        return texts.selected(ids.length);
      }
      const labels = ids.map((id) => labelMap.get(id) ?? id);
      if (labels.length <= 2) {
        return labels.join(', ');
      }
      return `${labels[0]} +${labels.length - 1}`;
    }
    case 'text':
      return `${value.op === 'eq' ? '=' : '~'} ${value.text?.trim() || '…'}`;
    case 'number': {
      const op = value.op === 'lte' ? '≤' : value.op === 'eq' ? '=' : '≥';
      return value.number === null || value.number === undefined ? '…' : `${op} ${value.number}`;
    }
    case 'daterange': {
      if (value.from && value.to) {
        return `${formatDay(value.from)}–${formatDay(value.to)}`;
      }
      if (value.from) {
        return texts.from(formatDay(value.from));
      }
      if (value.to) {
        return texts.to(formatDay(value.to));
      }
      return texts.all;
    }
  }
}
